// ThreadEntryV2 resolve conflict
import React from 'react';
import styled, { CSSObject } from '@emotion/styled';
import PropTypes from 'prop-types';
import he from 'he';

import { getBackgroundTheme, getBorders, getColor, getTextColor } from '../../style';
import RepliedIcon from '../../atoms/icons/Replied';
import LockedIcon from '../../atoms/icons/Locked';
import Avatar from '../../atoms/Avatar';
import { commonIcons } from '../icons/materialIcons';
import { CustomStyledProps } from 'style/style';
import breakpoints from 'theme/material/breakpoints';

const Entry = styled('div')<{ isOpen?: boolean; isLastEntry?: boolean }>(({ isOpen, isLastEntry }) => ({
  display: 'flex',
  padding: '10px',
  justifyContent: 'space-between',
  alignItems: 'center',
  alignSelf: 'stretch',
  position: 'relative' as const,
  overflow: 'hidden',
  cursor: 'pointer',
  background: isOpen ? getBackgroundTheme().block2 : getBackgroundTheme().block,
  borderBottom: isLastEntry ? 'none' : getBorders().PRIMARY,
  borderLeft: isOpen ? '5px solid #1C9EC9' : 'none',
  paddingRight: isOpen ? '6px' : '10px',
  minHeight: 90,
  [breakpoints.down(768.05)]: {
    background: getBackgroundTheme().block2,
    borderRight: 'none',
    borderLeft: 'none',
  },
}));

const ThreadStatus = styled('div')({
  display: 'flex',
  flex: '0 0 auto',
  width: 24,
  justifyContent: 'center',
});

const Preview = styled('div')({
  display: 'flex',
  flex: '1 1 auto',
  marginLeft: '6px',
  alignItems: 'end',
  justifyContent: 'space-between',
  marginTop: 'auto',
  minHeight: '70px',
});

const RightSideItems = styled('div')(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    gap: 6,
  })
);

const Details = styled('div')({
  display: 'flex',
  width: '98%',
  '> div:first-of-type': {
    flex: 1,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
});

const ThreadDetails = styled('div')({
  display: 'flex',
  flex: '1',
  minHeight: '70px',
});

const Snippet = styled('div')<{ isUnread?: boolean }>(() => ({
  display: 'flex',
  color: '#AFB0B1',
  fontSize: '13px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '18px',
  letterSpacing: '-0.3px',
}));

const ThreadMessage = styled('span')(
  () => ({
    ...getTextColor().DARK,
    '&:first-of-type': {
      flex: 1,
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
  }),
  ({ isLocked }: CustomStyledProps) => !isLocked && { width: '146px' }
);

const UnreadIcon = styled('div')(() => ({
  height: 12,
  width: 12,
  borderRadius: 7,
  backgroundColor: getColor().BRAND_1,
}));

const OnlineIcon = styled('div')(() => ({
  height: 12,
  width: 12,
  borderRadius: 7,
  backgroundColor: getColor().BRAND_1_ONLINE,
}));

const TimeStampStatus = styled('div')({
  marginBottom: '6px',
  textAlign: 'right',
  alignSelf: 'center',
});

const NewMessage = styled('span')({
  color: '#ffffff',
  borderRadius: 3,
  backgroundColor: '#1986D4',
  fontSize: 12,
  textAlign: 'center',
  lineHeight: '17px',
  padding: '0 5px',
  fontWeight: 'bold',
  marginRight: 5,
});

const Username = styled('div')<{ isUnread?: boolean }>(() => ({
  color: getTextColor().LIGHT_GRAY.color,
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '20px',
  letterSpacing: '-0.3px',
}));

const TimeStamp = styled('span')(() => ({
  color: getTextColor().LIGHT_GRAY.color,
  fontSize: '13px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '18px',
  letterSpacing: '-0.3px',
}));

const PhotoMessage = styled('span')({
  color: '#FFFFFF',
  backgroundColor: '#FF9900',
  padding: '2px 8px',
  fontSize: 10,
  borderRadius: 4,
  display: 'inline-block',
});

const renderMessage = (message: any, isPhoto = false, isDeleted = false, admin: boolean) => {
  if (isPhoto && !isDeleted) {
    return <PhotoMessage>IMG</PhotoMessage>;
  }

  if (isPhoto && isDeleted) {
    return 'This message is deleted';
  }

  const spanStyles: React.CSSProperties = admin
    ? {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical' as const,
        WebkitLineClamp: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
      }
    : {};

  const escapedMessage = admin ? message : he.encode(message);

  return <span style={spanStyles} dangerouslySetInnerHTML={{ __html: escapedMessage }} />;
};

interface ThreadEntryProps {
  username: any;
  avatar: any;
  message: any;
  isPhoto: any;
  isNew: any;
  iReplied: any;
  isLocked: any;
  isUnread: any;
  lastMsgDate: any;
  onThreadClick: any;
  selected: any;
  isSeveredAccount: any;
  accountType: any;
  isOnline: any;
  isDeleted: any;
  admin: boolean;
  isLastEntry?: boolean;
}

const ThreadEntry = ({
  username,
  avatar,
  message,
  isPhoto,
  isNew,
  iReplied,
  isLocked,
  isUnread,
  lastMsgDate,
  onThreadClick,
  selected,
  isSeveredAccount,
  accountType,
  isOnline,
  isDeleted,
  admin,
  isLastEntry,
}: ThreadEntryProps): any => {
  return (
    <Entry onClick={onThreadClick} isOpen={selected} isLastEntry={isLastEntry}>
      <Avatar
        src={avatar}
        width={68}
        shape="circle"
        isAdmin={admin}
        customStyle={{
          borderRadius: '65px',
          border: '1.219px solid var(--2024-Dark-Mode-DK-Primary-Border, #848484)',
        }}
      />
      {isSeveredAccount ? (
        <Preview>
          <Details
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            isNew={isNew}
            isUnread={isUnread || isLocked}
          >
            <div>{accountType}</div>
          </Details>
        </Preview>
      ) : (
        <ThreadDetails>
          <Preview>
            <div style={{ display: 'inline-block' }}>
              <Details>
                <Username isUnread={isUnread || isNew || isLocked}>
                  {username} {admin && <commonIcons.check.badge />}
                </Username>
              </Details>
              <Snippet isUnread={isUnread || isNew || isLocked}>
                <ThreadMessage isLocked={isLocked}>
                  {iReplied && <RepliedIcon />}

                  {isLocked ? 'Conversation locked' : renderMessage(message, isPhoto, isDeleted, admin)}
                </ThreadMessage>
              </Snippet>
            </div>
          </Preview>
          <RightSideItems>
            <TimeStampStatus>
              {isNew && <NewMessage>New</NewMessage>}
              <TimeStamp>{lastMsgDate}</TimeStamp>
            </TimeStampStatus>
            <ThreadStatus>
              {isLocked && <LockedIcon unread={isUnread} locked={isLocked} />}
              {!isLocked && isUnread && <UnreadIcon />}
              {!isLocked && !isUnread && isOnline && <OnlineIcon />}
            </ThreadStatus>
          </RightSideItems>
        </ThreadDetails>
      )}
    </Entry>
  );
};

ThreadEntry.propTypes = {
  username: PropTypes.string.isRequired,
  avatar: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  iReplied: PropTypes.bool.isRequired,
  isNew: PropTypes.bool.isRequired,
  isLocked: PropTypes.bool.isRequired,
  isUnread: PropTypes.bool.isRequired,
  lastMsgDate: PropTypes.string.isRequired,
  onThreadClick: PropTypes.func.isRequired,
  selected: PropTypes.bool.isRequired,
};

export default React.memo(ThreadEntry);
